import * as dbIssues from 'serviceWorker/db/issues/issues';
import type { IssueEntity } from 'serviceWorker/repository/issue/entity';
import { issueEntityToModel } from 'serviceWorker/repository/issue/issueEntityToModel';
import { getCurrentUsers } from 'serviceWorker/services/users/getUsers';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import type { FilteredIssues, IssueListAddress, IssuesQuery } from 'shared/domain/issue/issueQuery';

/**
 * Issues stored in IndexedDB mapped to models. Users are read as stored, never
 * waited for: a read must not block on another entity's download.
 */
async function toIssueModels(entities: IssueEntity[]): Promise<IssueModel[]> {
  const users = await getCurrentUsers();
  return entities.map((entity) => issueEntityToModel(entity, users));
}

/** One issue, archived ones included; `undefined` when it is not stored. */
export async function getIssueModel(id: string): Promise<IssueModel | undefined> {
  const entity = await dbIssues.getOne(id);
  if (!entity) {
    return undefined;
  }
  const [model] = await toIssueModels([entity]);
  return model;
}

/** The issue read by its id; throws when it is not stored. */
export function requireStoredIssue(id: string, issue: IssueModel | undefined): IssueModel {
  if (!issue) {
    throw new Error(`Cannot find issue with id ${id}`);
  }
  return issue;
}

/** One issue, archived ones included; rejects when it is not stored. */
export async function getExistingIssueModel(id: string): Promise<IssueModel> {
  return requireStoredIssue(id, await getIssueModel(id));
}

/** The page of issues matching the query and how many match in total. */
export async function getFilteredIssueModels(query: IssuesQuery): Promise<FilteredIssues> {
  const { items, total } = await dbIssues.getFiltered(query);
  return { items: await toIssueModels(items), total };
}

/** The issues matching the query, as positions for the previous / next navigation. */
export function getFilteredIssueAddresses(query: IssuesQuery): Promise<IssueListAddress[]> {
  return dbIssues.getFilteredIdList(query);
}

/** Every issue of the inspection, archived ones included (indexed read). */
export async function getIssueModelsOfInspection(inspectionId: string, timezone: string): Promise<IssueModel[]> {
  const { items } = await dbIssues.getByInspectionForView(inspectionId, timezone);
  return toIssueModels(items);
}
